import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '@/styles/pages.css';
import { ACCESS_LABEL, initials } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useAuth, useCurrentUser, type SignInMethods } from '@/auth/AuthContext';
import { EmptyState } from '@/components/EmptyState';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { SignaturePad } from '@/components/signature/SignaturePad';
import { useAsync } from '@/hooks/useAsync';
import type { ApprovalPreview } from '@/types/claims';

/**
 * The user's own record.
 *
 * Employee ID is editable: it is a fact about the person, it is printed on
 * every claim they file, and needing an admin to fix a typo in it helped
 * nobody. Department, position, employment level, reporting line and system
 * access are deliberately not editable here — each one changes how claims route
 * or who may approve them, so they belong to the audited admin functions rather
 * than to a self-service form.
 */
export function ProfilePage(): ReactNode {
  const user = useCurrentUser();
  const { refresh, methods } = useAuth();
  const { toast } = useToast();
  const [editingEmpId, setEditingEmpId] = useState(false);
  const [empId, setEmpId] = useState(user.empId);
  const [savingEmpId, setSavingEmpId] = useState(false);

  const saveEmpId = useCallback(async (): Promise<void> => {
    const value = empId.trim();
    if (!value) {
      toast('Enter your Employee ID.', 'err');
      return;
    }
    if (value === user.empId) {
      setEditingEmpId(false);
      return;
    }
    setSavingEmpId(true);
    try {
      await api.session.updateEmpId({ empId: value });
      // The session carries the id printed on every new claim, so it has to
      // catch up here or the form keeps filling in the old one.
      await refresh();
      toast('Employee ID updated.', 'ok');
      setEditingEmpId(false);
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSavingEmpId(false);
    }
  }, [empId, refresh, toast, user.empId]);
  const preview = useAsync<ApprovalPreview>(() => api.directory.approvalPreview<ApprovalPreview>({}), []);

  return (
    <>
      <div className="panel">
        <h3>My Profile</h3>
        <div className="profile-head">
          <div className="avatar lg" aria-hidden="true">
            {initials(user.name)}
          </div>
          <div>
            <b className="profile-name">{user.name}</b>
            <span className="profile-sub">
              {user.position} · {user.department}
            </span>
          </div>
        </div>

        <dl className="detail-grid">
          <div className="detail-item">
            <dt>Employee ID</dt>
            <dd>
              {editingEmpId ? (
                <div className="empid-edit">
                  <input
                    aria-label="Employee ID"
                    value={empId}
                    maxLength={40}
                    onChange={(e) => setEmpId(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => void saveEmpId()}
                    disabled={savingEmpId}
                  >
                    {savingEmpId ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setEmpId(user.empId);
                      setEditingEmpId(false);
                    }}
                    disabled={savingEmpId}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <span className="empid-edit">
                  {user.empId}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditingEmpId(true)}
                  >
                    Edit
                  </button>
                </span>
              )}
            </dd>
          </div>
          <Detail label="Company Email" value={user.email} />
          <Detail label="Department" value={user.department} />
          <Detail label="Position" value={user.position} />
          <Detail label="Employment Level" value={user.employmentLevel} />
          <Detail label="System Access" value={ACCESS_LABEL[user.systemAccess]} />
        </dl>

        <p className="hint">
          Your Employee ID is printed on every claim you file, so you can correct it yourself.
          Department, position, employment level and reporting line determine how your claims are
          routed for approval — to change any of those, contact the Main Access holder.
        </p>
      </div>

      <div className="panel">
        <h3>How my claims are routed</h3>
        {preview.loading ? (
          <LoadingBlock label="Working out your approval chain…" />
        ) : preview.error ? (
          <EmptyState icon="⚠️" title="Could not load your approval chain." description={preview.error} />
        ) : preview.data?.executiveRoute ? (
          <p className="route-line">
            <b>Executive route.</b> Your claims skip Manager and Lead approval and go straight to
            Finance for review.
          </p>
        ) : preview.data && preview.data.steps.length > 0 ? (
          <ol className="route-chain">
            {preview.data.steps.map((s) => (
              <li key={s.userId}>
                <span className="route-role">{s.role.replace(' By', '')}</span>
                <b>{s.name}</b>
              </li>
            ))}
            <li>
              <span className="route-role">Then</span>
              <b>Finance Review &amp; Approval</b>
            </li>
          </ol>
        ) : (
          <p className="route-line">
            You have no approver configured above you, so your claims go straight to Finance for
            review.
          </p>
        )}
      </div>

      <SignaturePad />

      <div className="panel">
        <h3>Sign-in and password</h3>
        <p className="hint">{signInHint(methods)}</p>
        {methods?.password === false ? null : (
          <Link className="btn btn-ghost" to="/change-password">
            Change password
          </Link>
        )}
      </div>
    </>
  );
}

function signInHint(methods: SignInMethods | null): string {
  const ways: string[] = [];
  if (methods?.microsoft) ways.push('your Microsoft 365 account');
  if (methods?.password !== false) ways.push('a password');
  if (methods?.emailCode !== false) ways.push('a code emailed to your company address');
  if (ways.length === 0) return 'Sign-in methods are managed by your administrator.';
  const list = ways.length === 1 ? ways[0]! : `${ways.slice(0, -1).join(', ')} or ${ways[ways.length - 1]}`;
  return `You can sign in with ${list}.`;
}

function Detail({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="detail-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
